import axios, { AxiosResponse } from 'axios';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL
  : process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:5000/api';

// Token management
const getToken = (): string | null => {
  return localStorage.getItem('admin_token');
};

const setToken = (token: string): void => {
  localStorage.setItem('admin_token', token);
};

const removeToken = (): void => {
  localStorage.removeItem('admin_token');
};

// Types
export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    id: number;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    language: string;
    isActive: boolean;
    role: 'admin' | 'bearbeiter';
  };
}

export interface AdminUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  language: string;
  is_active: boolean;
  role: 'admin' | 'bearbeiter';
  permissions?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface Appointment {
  id: number;
  patient_id?: number | null;
  patient_name: string;
  patient_email: string;
  patient_phone: string;
  provider_id: number;
  provider_name: string;
  procedure_id: number;
  procedure_name: string;
  operatory_id: number;
  operatory_name: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled' | 'no_show';
  notes?: string;
  language: string;
  google_calendar_event_id?: string;
  // Welcher Google-Kalender den Termin synced. 'primary' = bisheriger
  // (Bestand), 'secondary' = neuer Hauptkalender (info@seadent.es).
  // Fehlt das Feld auf Bestandsdaten → Backend behandelt als 'primary'.
  google_calendar_target?: 'primary' | 'secondary';
  source?: 'admin' | 'website';
  insurance?: string;
  intake_token?: string;
  intake_status?: 'pending' | 'submitted';
  created_at: string;
  updated_at: string;
  // Lebenszyklus-Events (gebucht / umgebucht / abgesagt) — nur von
  // GET /patients/:id geliefert, sonst undefiniert.
  timeline?: AppointmentTimelineEvent[];
}

export interface AppointmentTimelineEvent {
  action: 'create' | 'patient_reschedule' | 'cancel';
  changed_at: string;
  changed_by: string;
  previous_date: string | null;
  previous_time: string | null;
}

// Intake form (admin view) — mirrors server/models/IntakeForm.js
export interface IntakeFormAdmin {
  id: number;
  appointment_id: number;
  language: string;
  personal: Record<string, string>;
  health: {
    cardiovascular: Record<string, 'yes' | 'no' | 'unsure' | null>;
    general: Record<string, 'yes' | 'no' | 'unsure' | null>;
    infectious: Record<string, 'yes' | 'no' | 'unsure' | null>;
    allergies: Record<string, 'yes' | 'no' | 'unsure' | null>;
  };
  other_diseases_text: string;
  other_infectious_text: string;
  metals_allergy_text: string;
  pregnancy: { is_pregnant: string | null; month: string };
  xray: { had_xray: string | null; when: string };
  medications: Array<{ name: string; since: string }>;
  special_medications: Record<string, { value: string | null; since: string }>;
  major_surgery: { value: string | null; date: string };
  consent_accepted: boolean;
  signature_data_url: string | null;
  signed_place: string;
  signed_at: string | null;
  submitted_at: string | null;
  pdf_path: string | null;
  mail_status: 'pending' | 'sent' | 'failed';
  mail_attempts: number;
  mail_last_at: string | null;
  mail_last_error: string | null;
  mail_to: string | null;
  created_at: string;
  updated_at: string;
}

export interface IntakeAdminResponse {
  success: boolean;
  appointment: {
    id: number;
    patient_name: string;
    intake_token: string;
    intake_status: 'pending' | 'submitted';
  };
  form: IntakeFormAdmin | null;
}

export interface IntakeResendResponse {
  success: boolean;
  mail_status: 'pending' | 'sent' | 'failed';
  mail_attempts: number;
  mail_last_at: string | null;
  mail_to: string | null;
  error: string | null;
}

export interface IntakeInvitationResponse {
  success: boolean;
  mail_to?: string;
  error?: string;
}

// Row in the global intake-forms admin list. Joined with appointment summary.
export interface IntakeListItem {
  id: number;
  appointment_id: number;
  language: string;
  submitted_at: string | null;
  signed_place: string;
  pdf_available: boolean;
  mail_status: 'pending' | 'sent' | 'failed';
  mail_attempts: number;
  mail_last_at: string | null;
  mail_last_error: string | null;
  mail_to: string | null;
  patient_name: string;
  patient_email: string;
  appointment_date: string;
  start_time: string;
  intake_status: 'pending' | 'submitted';
}

export interface IntakeListResponse {
  success: boolean;
  forms: IntakeListItem[];
}

export interface AuditLog {
  id: number;
  appointment_id: number;
  action: string;
  old_data: any;
  new_data: any;
  changed_by: string;
  changed_at: string;
}

export type ProviderRole = 'dentist' | 'hygienist';

export interface Provider {
  id: number;
  name: string;
  email: string;
  phone: string;
  specialization: string;
  role: ProviderRole;
  google_calendar_id?: string;
  is_active: boolean;
}

export type LocalizedString = Record<'de' | 'en' | 'es' | 'ru', string>;

export interface Procedure {
  id: number;
  key?: string;
  name: LocalizedString | string;       // multilingual; string for legacy
  description?: LocalizedString | string;
  duration: number;
  color: string;
  required_roles?: ProviderRole[];
  is_active: boolean;
  bookable?: boolean;
  /** Online-Buchung: Behandlung muss bis dahin beendet sein ('HH:mm'), null = Praxisschluss. */
  latest_end_time?: string | null;
  /** Online-Buchung: volle Arbeitstage Karenzzeit; null = Praxis-Default, 0 = kurzfristig buchbar. */
  lead_time_working_days?: number | null;
}

/** Resolve a localized string in given locale, with fallback chain. */
export const resolveLocalized = (v: LocalizedString | string | undefined, locale: string = 'de'): string => {
  if (!v) return '';
  if (typeof v === 'string') return v;
  const obj = v as Record<string, string>;
  return obj[locale] || obj.de || obj.en || obj.es || obj.ru || Object.values(obj).find(Boolean) || '';
};

export interface Operatory {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

export interface HealthStatus {
  status: string;
  timestamp: string;
  uptime: number;
  googleCalendar: boolean;
  // true nur auf der Demo-Instanz (Env DEMO_MODE) — steuert das Demo-Banner.
  demoMode?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  total?: number;
}

// ----- Patient -----
export interface Patient {
  id: number;
  patient_number: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  email: string | null;
  email_normalized: string | null;
  phone_primary: string | null;
  phone_secondary: string | null;
  date_of_birth: string | null;
  place_of_birth: string;
  nie: string;
  occupation: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  doctor_name: string;
  doctor_address: string;
  doctor_phone: string;
  insurance: string | null;
  preferred_language: 'de' | 'en' | 'es' | 'ru';
  notes: string;
  intake_form_id: number | null;
  archived: boolean;
  archived_at: string | null;
  archived_reason: string | null;
  source: 'website' | 'admin' | 'migration' | 'import';
  created_at?: string;
  updated_at?: string;
}

export type MarketingConsentStatus = 'granted' | 'denied' | 'revoked' | 'never_asked';

export interface PatientListItem extends Patient {
  appointment_count: number;
  last_appointment_date: string | null;
  marketing_consent?: {
    email: MarketingConsentStatus;
    whatsapp: MarketingConsentStatus;
    phone: MarketingConsentStatus;
  };
}

export interface PatientListParams {
  search?: string;
  archived?: 'true' | 'false';
  language?: string;
  hasIntake?: 'true' | 'false';
  limit?: number;
  offset?: number;
}

export interface PatientListResponse {
  success: boolean;
  data: PatientListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface PatientDuplicateSuspect {
  id: number;
  patient_number: string;
  full_name: string;
  email: string | null;
  phone_primary: string | null;
}

export interface PatientDetail extends Patient {
  appointments: Appointment[];
  intake_form: IntakeFormAdmin | null;
  intake_pdf_available: boolean;
  duplicate_suspects: PatientDuplicateSuspect[];
}

export interface PatientAuditLog {
  id: number;
  patient_id: number;
  action: string;
  old_data: any;
  new_data: any;
  changed_by: string;
  changed_at: string;
}

// ----- Kommunikation (Mini-CRM) -----
// Spiegel von server/models/Communication.js — einheitliches Kommunikationslog,
// aus dem die Timeline in der Patientenakte liest (docs/CRM-PLAN.md).

export type CommunicationKind =
  | 'appointment_status'
  | 'reminder'
  | 'recall'
  | 'intake_invitation'
  | 'intake_confirmation'
  | 'invoice'
  | 'practice_notification'
  | 'booking_request'
  | 'contact_form'
  | 'manual'
  | 'inbound_email'
  // SmartAI-Panel (Public Site): strukturierte Anliegen + unbeantwortete Fragen
  | 'smartai_question'
  | 'smartai_callback'
  | 'smartai_document_request'
  | 'smartai_manage_link';

// --- SmartAI (KI-Rezeption): Wissensbasis-Eintrag + Betriebs-Settings --------

export type SmartAIKnowledgeCategory =
  | 'hours' | 'directions' | 'team' | 'services' | 'pricing'
  | 'insurance' | 'faq' | 'policy' | 'custom';

export interface SmartAIKnowledgeEntry {
  id: number;
  category: SmartAIKnowledgeCategory;
  title: string;
  /** Deutscher Referenztext — das LLM übersetzt in die Besuchersprache. */
  content: string;
  /** Optionale exakte Übersetzungen, die die LLM-Übersetzung überstimmen. */
  overrides: Partial<Record<'en' | 'es' | 'ru', string>> | null;
  enabled: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export type SmartAIKnowledgeInput = Omit<SmartAIKnowledgeEntry, 'id' | 'created_at' | 'updated_at'>;

export interface SmartAISettings {
  /** 'preview' = Panel nur mit ?smartai=preview sichtbar; 'public' = für alle. */
  visibility: 'preview' | 'public';
  /** 'classic' = bisheriges Panel; 'v2' = Buchung und Terminverwaltung im Panel. */
  layout: 'classic' | 'v2';
  /** Gespeicherte Werte — leer heißt: die Server-Env gilt. */
  provider: string;
  model: string;
  baseUrl: string;
  /** Denkaufwand von Reasoning-Modellen; leer = Anbieter-Standard. */
  reasoningEffort: SmartAIReasoningEffort;
  /** Was tatsächlich wirkt, inklusive Env-Fallback. */
  effectiveProvider: string;
  effectiveModel: string;
  effectiveBaseUrl: string;
  effectiveReasoningEffort: SmartAIReasoningEffort;
  /** Der Schlüssel selbst wird nie ausgeliefert — nur diese drei Angaben. */
  hasApiKey: boolean;
  apiKeyMasked: string;
  apiKeyFromEnv: boolean;
  defaultModel: string;
}

/**
 * Denkaufwand eines Reasoning-Modells. Leer = Parameter wird nicht
 * mitgeschickt (Anbieter-Standard).
 */
export type SmartAIReasoningEffort = '' | 'none' | 'low' | 'medium' | 'high';

/** Schreibbare Felder. apiKey: '' löscht den gespeicherten Schlüssel. */
export interface SmartAISettingsPatch {
  visibility?: 'preview' | 'public';
  layout?: 'classic' | 'v2';
  provider?: string;
  model?: string;
  baseUrl?: string;
  apiKey?: string;
  reasoningEffort?: SmartAIReasoningEffort;
}

export interface SmartAILlmTestResult {
  status: 'done' | 'failed' | 'skipped';
  model: string;
  baseUrl: string;
  latency_ms: number | null;
  reply: string;
  error: string | null;
  /** Wie viele der erzeugten Tokens ins interne Nachdenken gingen. */
  reasoningTokens?: number;
}

// 'unknown' = Prozess starb zwischen SMTP-Call und Status-Update —
// darf NIE als Resend-Kandidat angeboten werden (Doppelversand-Gefahr).
export type CommunicationDeliveryStatus = 'sending' | 'sent' | 'failed' | 'skipped' | 'unknown';

// Inbox-Zustand eingehender Mails (Phase 2a): 'unmatched' = kein Patient
// zugeordnet; null bei Outbound-Einträgen.
export type CommunicationInboxState = 'new' | 'read' | 'answered' | 'unmatched';

// Anhang-Metadaten eines Inbound-Eintrags — die Datei selbst liegt auf dem
// Server (uploads/inbound/), Download über fetchCommunicationAttachment().
export interface CommunicationAttachment {
  filename: string;
  mime: string;
  size: number;
  path: string;
}

export interface Communication {
  id: number;
  direction: 'outbound' | 'inbound';
  channel: 'email' | 'smartai';
  kind: CommunicationKind;
  patient_id: number | null;
  ref_type: string | null; // 'appointment' | 'invoice' | 'intake_form' | 'recall_schedule'
  ref_id: number | null;
  to: string | null;
  from_account: 'info' | 'billing';
  subject: string;
  body_text: string;
  language: string | null;
  delivery_status: CommunicationDeliveryStatus;
  error: string | null;
  message_id: string | null;
  trigger: string; // 'cron' | 'manual' | 'patient' | 'system'
  sent_by: string;
  resend_of: number | null;
  source: string; // 'live' | 'backfill:<quelle>'
  sent_at: string | null;
  created_at: string;
  updated_at?: string;
  // ----- Inbound-Felder (Phase 2a) — bei Outbound-Einträgen null/leer -----
  inbox_state: CommunicationInboxState | null;
  from_address: string | null;
  from_name: string | null;
  attachments: CommunicationAttachment[];
  attachments_skipped: number;
  eml_path: string | null;
  thread_id: string | null;
}

export interface CommunicationListResult {
  items: Communication[];
  total: number;
}

// Zähler der Inbox-Filter-Pills (immer über alle Zustände, unabhängig vom Filter).
export interface InboxCounts {
  new: number;
  read: number;
  answered: number;
  unmatched: number;
  total: number;
}

export interface InboxListResult {
  items: Communication[];
  counts: InboxCounts;
}

export interface AssignCommunicationResult {
  communication: Communication;
  // true, wenn die Absenderadresse als Zweit-E-Mail am Patienten gespeichert wurde.
  alternate_saved: boolean;
}

export interface SendCommunicationPayload {
  patient_id: number | null;
  to: string;
  subject: string;
  body: string;
  language?: string;
  // Antwort auf eine Inbound-Mail: hängt die Antwort in den Thread und
  // markiert die Inbound-Mail als 'answered'.
  reply_to_communication_id?: number;
}

export interface SendCommunicationResult {
  communication: Communication | null;
  messageId: string | null;
  // true, wenn die Empfängeradresse von der in der Patientenakte abweicht —
  // rein informativ, blockiert den Versand nicht.
  recipient_drift: boolean;
}

export interface ResendCommunicationResult {
  communication: Communication | null;
  messageId: string | null;
}

export interface CommunicationFailedCount {
  count: number;
  patient_ids: number[];
}

// ----- Vendor-Feature-Flags (Paket-Steuerung) -----

export interface FeaturesResult {
  features: Record<string, boolean>;
  is_vendor: boolean;
}

// ----- Treatment Notes -----

export type TreatmentNoteKind = 'visit' | 'note';

export interface TreatmentNote {
  id: number;
  patient_id: number;
  appointment_id: number | null;
  kind: TreatmentNoteKind;
  body: string;
  chief_complaint: string;
  findings: string;
  treatment_performed: string;
  plan: string;
  tooth_refs: string[];
  template_id: number | null;
  section_values: Record<string, any>;
  body_overridden: boolean;
  author_user_id: number | null;
  author_username: string;
  archived: boolean;
  archived_at: string | null;
  archived_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTreatmentNotePayload {
  appointment_id?: number | null;
  kind?: TreatmentNoteKind;
  body?: string;
  chief_complaint?: string;
  findings?: string;
  treatment_performed?: string;
  plan?: string;
  tooth_refs?: string[];
  template_id?: number | null;
  section_values?: Record<string, any>;
  body_overridden?: boolean;
  locale?: SupportedLocale;
  title?: string;
}

export interface UpdateTreatmentNotePayload {
  appointment_id?: number | null;
  kind?: TreatmentNoteKind;
  body?: string;
  chief_complaint?: string;
  findings?: string;
  treatment_performed?: string;
  plan?: string;
  tooth_refs?: string[];
  template_id?: number | null;
  section_values?: Record<string, any>;
  body_overridden?: boolean;
  locale?: SupportedLocale;
  title?: string;
}

export interface TreatmentNoteAuditLog {
  id: number;
  note_id: number;
  action: string;
  old_data: any;
  new_data: any;
  changed_by: string;
  changed_at: string;
}

// ----- Treatment Note Templates (Phase A.5) -----

export type SupportedLocale = 'de' | 'es' | 'en';
export type I18nString = Partial<Record<SupportedLocale, string>>;

export type SectionType =
  | 'single_choice'
  | 'multi_choice'
  | 'tooth_chart'
  | 'text_freeform'
  | 'chips_indication';

export interface SectionOption {
  value: string;
  label: I18nString;
}

export interface ToothChartConfig {
  allow_deciduous?: boolean;
  jaws?: Array<'OK' | 'UK'>;
}

export interface TextFreeformConfig {
  placeholder?: string | I18nString;
}

export interface ProcedureMappingRule {
  when?: {
    teeth_count_min?: number;
    values_count_min?: number;
    value_in?: string[];
    value_eq?: string;
  };
  procedure_id: number;
  quantity?: number;
  quantity_from?: 'teeth_count' | 'values_count';
}

export interface TemplateSection {
  key: string;
  type: SectionType;
  label: I18nString;
  doc_template: I18nString;
  options?: SectionOption[];
  config?: ToothChartConfig | TextFreeformConfig | Record<string, any>;
  procedure_mapping?: { rules: ProcedureMappingRule[] };
}

export interface TemplateProcedureMapping {
  procedure_id: number;
  quantity?: number;
}

export type TemplateCategory =
  | 'prophylaxis' | 'restorative' | 'endodontics' | 'implant'
  | 'aesthetics' | 'consultation' | 'emergency' | 'diagnostic' | 'other';

export interface TreatmentNoteTemplate {
  id: number;
  slug: string;
  name: I18nString;
  category: TemplateCategory;
  language_default: SupportedLocale;
  header_template: I18nString;
  sections: TemplateSection[];
  procedure_mappings?: TemplateProcedureMapping[];
  version: number;
  archived: boolean;
  archived_at: string | null;
  archived_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplatePayload {
  slug: string;
  name: I18nString;
  category?: TemplateCategory;
  language_default?: SupportedLocale;
  header_template?: I18nString;
  sections: TemplateSection[];
  procedure_mappings?: TemplateProcedureMapping[];
}

export interface UpdateTemplatePayload {
  slug?: string;
  name?: I18nString;
  category?: TemplateCategory;
  language_default?: SupportedLocale;
  header_template?: I18nString;
  sections?: TemplateSection[];
  procedure_mappings?: TemplateProcedureMapping[];
}

// ----- Procedure Suggestions (Phase D) -----

export interface ProcedureSuggestion {
  procedure_id: number;
  procedure: {
    id: number;
    key: string;
    name: I18nString;
    duration: number | null;
  };
  quantity: number;
  sources: string[];
  tooth_numbers?: string[];
}

export interface Indication {
  value: string;
  label: I18nString;
}

// ----- Audio Files (Phase B) -----

export type TranscriptStatus = 'pending' | 'done' | 'failed' | 'skipped';

export interface AudioFile {
  id: number;
  note_id: number;
  patient_id: number;
  file_path: string;
  original_name: string;
  size_bytes: number;
  mime_type: string;
  duration_sec: number | null;
  transcript_status: TranscriptStatus;
  transcript_text: string;
  transcript_model: string;
  transcript_tokens: number | null;
  transcript_latency_ms: number | null;
  transcript_error: string | null;
  locale: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AudioConsent {
  version: string;
  granted: boolean | null;
  granted_at: string | null;
  ip_at_consent: string | null;
  user_agent_at_consent: string | null;
  granted_by_user?: string | null;
}

export interface AudioConsentResponse {
  patient_id: number;
  form_id: number | null;
  audio_recording_consent: AudioConsent;
}

export interface ConflictCheckParams {
  date: string;
  startTime: string;
  endTime: string;
  providerId: number;
  operatoryId: number;
  excludeId?: number;
}

// Native Google Calendar Event — what /api/google-calendar/events returns per item.
// Subset of the Google API event resource; only fields the UI uses.
export interface GoogleCalendarEvent {
  id: string;
  summary?: string | null;
  description?: string | null;
  location?: string | null;
  start?: { dateTime?: string; date?: string; timeZone?: string } | null;
  end?: { dateTime?: string; date?: string; timeZone?: string } | null;
  status?: 'confirmed' | 'tentative' | 'cancelled' | null;
  htmlLink?: string | null;
  colorId?: string | null;
  created?: string | null;
  updated?: string | null;
  creator?: { email?: string; displayName?: string } | null;
  organizer?: { email?: string; displayName?: string } | null;
  attendees?: Array<{ email?: string; responseStatus?: string }> | null;
  extendedProperties?: { private?: Record<string, string>; shared?: Record<string, string> } | null;
  appointmentId?: string | null; // backend-extracted convenience field
  sourceCalendarId?: string | null; // gesetzt für Events aus read-only Zusatzkalendern
  readOnly?: boolean; // true → Event stammt aus einem Zusatzkalender, keine Mutationen erlaubt
}

// ----- Billing / Invoices types -----

export type InvoiceType = 'offer' | 'invoice' | 'cancellation';
export type InvoiceStatus = 'draft' | 'sent' | 'accepted' | 'rejected' | 'issued' | 'paid' | 'canceled';
export type InvoiceLanguage = 'de' | 'es' | 'en';

export interface InvoiceBillingAddress {
  name: string;
  street?: string;
  postal_code?: string;
  city?: string;
  country?: string;
  nie?: string;
  email?: string;
  phone?: string;
}

export interface InvoiceVatBreakdownRow {
  rate: number;
  net_cents: number;
  vat_cents: number;
}

export interface InvoiceDocument {
  id: number;
  type: InvoiceType;
  status: InvoiceStatus;
  number: string | null;
  number_seq: number | null;
  patient_id: number;
  patient_number?: string | null;
  billing_address: InvoiceBillingAddress;
  language: InvoiceLanguage;
  currency: string;
  issue_date: string;
  due_date: string | null;
  valid_until: string | null;
  subtotal_cents: number;
  vat_total_cents: number;
  discount_cents: number;
  total_cents: number;
  vat_breakdown: InvoiceVatBreakdownRow[];
  notes_header: string;
  notes_footer: string;
  payment_terms_text: string;
  bank_details_text: string;
  source_offer_id: number | null;
  cancels_id: number | null;
  canceled_by_id: number | null;
  appointment_ids: number[];
  pdf_path: string | null;
  finalized_at: string | null;
  paid_at: string | null;
  payment_method: string | null;
  internal_note: string;
  created_at?: string;
  updated_at?: string;
}

export interface InvoiceItem {
  id: number;
  document_id: number;
  position: number;
  source: 'catalog' | 'procedure' | 'free';
  source_id: number | null;
  code: string;
  label: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_cents: number;
  discount_percent: number;
  vat_rate: number;
  line_net_cents: number;
  line_vat_cents: number;
  line_total_cents: number;
}

export interface InvoiceDocumentDetail extends InvoiceDocument {
  items: InvoiceItem[];
  patient: Patient | null;
}

export interface InvoiceListParams {
  type?: InvoiceType | InvoiceType[];
  status?: InvoiceStatus | InvoiceStatus[];
  patient_id?: number;
  q?: string;
  from?: string;
  to?: string;
}

export interface CreateInvoicePayload {
  patient_id: number;
  type?: InvoiceType;
  language?: InvoiceLanguage;
  issue_date?: string;
  notes_header?: string;
  notes_footer?: string;
  appointment_ids?: number[];
  source_offer_id?: number;
  items?: Partial<InvoiceItem>[];
}

export interface InvoiceCatalogItem {
  id: number;
  code: string;
  name: { de: string; es: string; en: string };
  description: { de: string; es: string; en: string };
  default_unit: string;
  default_price_cents: number;
  default_vat_rate: number;
  category: 'implantology' | 'prophylaxis' | 'aesthetic' | 'oral_surgery' | 'orthodontics' | 'consultation' | 'other';
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
}

export interface InvoiceEmailPreview {
  to: string;
  language: InvoiceLanguage;
  subject: string;
  body: string;
}

export interface BillingSmtpSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  from_name: string;
  passSet: boolean;
}

export interface InvoiceCounter {
  prefix: string;
  next: number;
  pad: number;
  last_assigned_at: string | null;
}

export interface InvoiceCounterPreview {
  type: InvoiceType;
  number: string;
  seq: number;
  prefix: string;
  pad: number;
}

export interface BillingSettings {
  issuer: {
    name: string;
    legal_form: string;
    nif: string;
    vat_id: string;
    address_line1: string;
    address_line2: string;
    postal_code: string;
    city: string;
    country: string;
    phone: string;
    email: string;
    website: string;
    iban: string;
    bic: string;
    bank_name: string;
    medical_director: string;
  };
  defaults: {
    vat_rate: number;
    due_days: number;
    offer_valid_days: number;
    language: InvoiceLanguage;
    currency: string;
  };
  payment_terms_text: { de: string; es: string; en: string };
  bank_details_text: { de: string; es: string; en: string };
  default_invoice_footer: { de: string; es: string; en: string };
  vat_exemption_text: { de: string; es: string; en: string };
  quick_action_catalog_ids: number[];
  email_templates: Record<InvoiceLanguage, Record<InvoiceType, { subject: string; body: string }>>;
}

// === Auto-Import eingebundener Google-Kalender ===

// Pro-Kalender-Konfiguration: für welchen Kalender mit welchen Standardwerten
// (Behandler/Behandlung/Raum) Events automatisch als App-Termine übernommen werden.
export interface CalendarAutoImportCalendarConfig {
  calendarId: string;          // '__main__' (Hauptkalender) oder eine Zusatzkalender-ID
  enabled: boolean;
  provider_id: number | null;
  procedure_id: number | null;
  operatory_id: number | null;
}

// Persistierte Einstellungen des Auto-Imports.
export interface CalendarAutoImportSettings {
  enabled: boolean;
  windowDaysBack: number;
  windowDaysAhead: number;
  calendars: CalendarAutoImportCalendarConfig[];
  lastRun: {
    at: string;
    converted: number;
    skipped: number;
    errors: number;
  } | null;
}

// Ein vom Backend bekanntgegebener Kalender (Haupt- oder Zusatzkalender),
// für den eine Auto-Import-Konfiguration angelegt werden kann.
export interface CalendarAutoImportCatalogEntry {
  calendarId: string;
  label: string;
  role: 'main' | 'extra';
}

// GET-Antwort: aktuelle Settings + Liste der verfügbaren Kalender.
export interface CalendarAutoImportCatalog {
  settings: CalendarAutoImportSettings;
  calendars: CalendarAutoImportCatalogEntry[];
}

// Ergebnis eines Auto-Import-Laufs.
export interface CalendarAutoImportRunSummary {
  converted: number;
  skipped: number;
  errors: Array<{ eventId?: string; calendarId?: string; summary?: string | null; message: string }>;
  dryRun: boolean;
  globallyDisabled: boolean;
  notReady: boolean;
  perCalendar: Array<{
    calendarId: string;
    converted: number;
    skipped: number;
    errors: number;
  }>;
}

class ApiService {
  private api;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          removeToken();
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginCredentials): Promise<AxiosResponse<LoginResponse>> {
    const response = await this.api.post('/auth/login', credentials);
    if (response.data.success && response.data.token) {
      setToken(response.data.token);
    }
    return response;
  }

  async requestMagicLink(email: string): Promise<AxiosResponse<{ success: boolean; message?: string }>> {
    return this.api.post('/auth/magic-link/request', { email });
  }

  async verifyMagicLink(token: string): Promise<AxiosResponse<Partial<LoginResponse> & { success: boolean; message?: string; code?: string }>> {
    const response = await this.api.post('/auth/magic-link/verify', { token });
    if (response.data.success && response.data.token) {
      setToken(response.data.token);
    }
    return response;
  }

  logout(): void {
    removeToken();
  }

  isAuthenticated(): boolean {
    return !!getToken();
  }

  // Appointment methods
  async getAppointments(params?: any): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments', { params });
  }

  async getAppointment(id: number): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.get(`/appointments/${id}`);
  }

  async getAppointmentAuditLogs(id: number): Promise<AxiosResponse<ApiResponse<AuditLog[]>>> {
    return this.api.get(`/appointments/${id}/audit-logs`);
  }

  async createAppointment(appointmentData: Partial<Appointment>): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.post('/appointments', appointmentData);
  }

  async updateAppointment(id: number, appointmentData: Partial<Appointment>): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.put(`/appointments/${id}`, appointmentData);
  }

  async deleteAppointment(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/appointments/${id}`);
  }

  async cancelAppointment(id: number): Promise<AxiosResponse<ApiResponse<Appointment>>> {
    return this.api.patch(`/appointments/${id}/cancel`);
  }

  // ----- Intake form (admin) -----
  async listIntakeForms(): Promise<AxiosResponse<IntakeListResponse>> {
    return this.api.get('/admin/intake');
  }

  async getIntake(appointmentId: number): Promise<AxiosResponse<IntakeAdminResponse>> {
    return this.api.get(`/admin/intake/${appointmentId}`);
  }

  async resendIntakeMail(appointmentId: number): Promise<AxiosResponse<IntakeResendResponse>> {
    return this.api.post(`/admin/intake/${appointmentId}/resend`);
  }

  /** Sends the intake link + QR code to the patient at the supplied address. */
  async sendIntakeInvitation(
    appointmentId: number,
    payload: { to: string; language?: string },
  ): Promise<AxiosResponse<IntakeInvitationResponse>> {
    return this.api.post(`/admin/intake/${appointmentId}/send-invitation`, payload);
  }

  /** Fetches the PDF as a Blob — caller decides whether to download or display. */
  async fetchIntakePdf(appointmentId: number): Promise<Blob> {
    const res = await this.api.get(`/admin/intake/${appointmentId}/pdf`, {
      responseType: 'blob',
      timeout: 30000, // PDF gen can take a moment on cold appointments
    });
    return res.data;
  }

  async checkConflicts(params: ConflictCheckParams): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments/conflicts', { params });
  }

  // Provider methods
  async getProviders(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Provider[]>>> {
    return this.api.get('/providers', { params: { activeOnly } });
  }

  async getProvider(id: number): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.get(`/providers/${id}`);
  }

  async createProvider(providerData: Partial<Provider>): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.post('/providers', providerData);
  }

  async updateProvider(id: number, providerData: Partial<Provider>): Promise<AxiosResponse<ApiResponse<Provider>>> {
    return this.api.put(`/providers/${id}`, providerData);
  }

  async deleteProvider(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/providers/${id}`);
  }

  // Procedure methods
  async getProcedures(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Procedure[]>>> {
    return this.api.get('/procedures', { params: { activeOnly } });
  }

  async getProcedure(id: number): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.get(`/procedures/${id}`);
  }

  async createProcedure(procedureData: Partial<Procedure>): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.post('/procedures', procedureData);
  }

  async updateProcedure(id: number, procedureData: Partial<Procedure>): Promise<AxiosResponse<ApiResponse<Procedure>>> {
    return this.api.put(`/procedures/${id}`, procedureData);
  }

  async deleteProcedure(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/procedures/${id}`);
  }

  // Operatory methods
  async getOperatories(activeOnly: boolean = true): Promise<AxiosResponse<ApiResponse<Operatory[]>>> {
    return this.api.get('/operatories', { params: { activeOnly } });
  }

  async getOperatory(id: number): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.get(`/operatories/${id}`);
  }

  async createOperatory(operatoryData: Partial<Operatory>): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.post('/operatories', operatoryData);
  }

  async updateOperatory(id: number, operatoryData: Partial<Operatory>): Promise<AxiosResponse<ApiResponse<Operatory>>> {
    return this.api.put(`/operatories/${id}`, operatoryData);
  }

  async deleteOperatory(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/operatories/${id}`);
  }

  // Google Calendar methods
  async syncGoogleCalendar(): Promise<AxiosResponse<any>> {
    return this.api.post('/google-calendar/sync');
  }

  // DEBUG: Fetch raw Google Calendar events
  async debugGoogleCalendarEvents(): Promise<AxiosResponse<any>> {
    return this.api.get('/google-calendar/debug-events');
  }

  // Share calendar with user's email
  async shareGoogleCalendar(userEmail: string, role: string = 'reader'): Promise<AxiosResponse<any>> {
    return this.api.post('/google-calendar/share-calendar', { userEmail, role });
  }

  // Get calendar permissions
  async getGoogleCalendarPermissions(): Promise<AxiosResponse<any>> {
    return this.api.get('/google-calendar/permissions');
  }

  // === Native Google-Calendar event CRUD (for the merged calendar view) ===

  async listGoogleEvents(from?: string, to?: string): Promise<AxiosResponse<ApiResponse<GoogleCalendarEvent[]>>> {
    return this.api.get('/google-calendar/events', { params: { from, to } });
  }

  async updateGoogleEvent(eventId: string, partial: Partial<GoogleCalendarEvent>): Promise<AxiosResponse<ApiResponse<GoogleCalendarEvent>>> {
    return this.api.patch(`/google-calendar/events/${encodeURIComponent(eventId)}`, partial);
  }

  async cancelGoogleEvent(eventId: string): Promise<AxiosResponse<ApiResponse<GoogleCalendarEvent>>> {
    return this.api.post(`/google-calendar/events/${encodeURIComponent(eventId)}/cancel`);
  }

  async confirmGoogleEvent(eventId: string): Promise<AxiosResponse<ApiResponse<GoogleCalendarEvent>>> {
    return this.api.post(`/google-calendar/events/${encodeURIComponent(eventId)}/confirm`);
  }

  async deleteGoogleEvent(eventId: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/google-calendar/events/${encodeURIComponent(eventId)}`);
  }

  async convertGoogleEventToAppointment(eventId: string, params: {
    provider_id: number;
    procedure_id: number;
    operatory_id: number;
    patient_name?: string;
    patient_email?: string;
    patient_phone?: string;
    source_calendar_id?: string; // Event stammt aus einem read-only Zusatzkalender
  }): Promise<AxiosResponse<ApiResponse<{ appointment: Appointment; eventId: string }>>> {
    return this.api.post(`/google-calendar/events/${encodeURIComponent(eventId)}/convert`, params);
  }

  async bulkConvertGoogleEvents(params: {
    provider_id: number;
    procedure_id: number;
    operatory_id: number;
    from?: string;
    to?: string;
    source_calendar_id?: string; // Import aus einem read-only Zusatzkalender
  }): Promise<AxiosResponse<ApiResponse<{ converted: number; skipped: number; errors: Array<{ eventId: string; summary: string | null; message: string }> }>>> {
    return this.api.post('/google-calendar/events/bulk-convert', params);
  }

  // ----- Auto-Import eingebundener Google-Kalender -----

  async getCalendarAutoImportSettings(): Promise<AxiosResponse<ApiResponse<CalendarAutoImportCatalog>>> {
    return this.api.get('/google-calendar/auto-import');
  }

  async updateCalendarAutoImportSettings(payload: {
    enabled?: boolean;
    windowDaysBack?: number;
    windowDaysAhead?: number;
    calendars?: CalendarAutoImportCalendarConfig[];
  }): Promise<AxiosResponse<ApiResponse<CalendarAutoImportSettings>>> {
    return this.api.put('/google-calendar/auto-import', payload);
  }

  async runCalendarAutoImport(dryRun?: boolean): Promise<AxiosResponse<ApiResponse<CalendarAutoImportRunSummary>>> {
    return this.api.post('/google-calendar/auto-import/run', { dryRun });
  }

  // ----- Patients -----
  async listPatients(params: PatientListParams = {}): Promise<AxiosResponse<PatientListResponse>> {
    return this.api.get('/patients', { params });
  }

  async getPatient(id: number): Promise<AxiosResponse<ApiResponse<PatientDetail>>> {
    return this.api.get(`/patients/${id}`);
  }

  async createPatient(payload: Partial<Patient>): Promise<AxiosResponse<ApiResponse<Patient>>> {
    return this.api.post('/patients', payload);
  }

  async updatePatient(id: number, payload: Partial<Patient>): Promise<AxiosResponse<ApiResponse<Patient>>> {
    return this.api.put(`/patients/${id}`, payload);
  }

  async archivePatient(id: number, reason?: string): Promise<AxiosResponse<ApiResponse<Patient>>> {
    return this.api.delete(`/patients/${id}`, { data: { reason } });
  }

  async unarchivePatient(id: number): Promise<AxiosResponse<ApiResponse<Patient>>> {
    return this.api.post(`/patients/${id}/unarchive`);
  }

  async mergePatients(sourceId: number, targetId: number): Promise<AxiosResponse<ApiResponse<{ source: Patient; target: Patient; moved: { appointments: number; intake: string } }>>> {
    return this.api.post(`/patients/${sourceId}/merge`, { targetId });
  }

  async getPatientAuditLogs(id: number): Promise<AxiosResponse<ApiResponse<PatientAuditLog[]>>> {
    return this.api.get(`/patients/${id}/audit-logs`);
  }

  // ----- Kommunikation (Mini-CRM) -----

  /** Kommunikations-Timeline eines Patienten, neueste zuerst (Capability "patients"). */
  async listPatientCommunications(
    patientId: number,
    params?: { limit?: number; before_id?: number },
  ): Promise<AxiosResponse<ApiResponse<CommunicationListResult>>> {
    return this.api.get(`/patients/${patientId}/communications`, { params });
  }

  /** Freie Mail an einen Patienten (kind='manual', immer info@) — Capability "communications". */
  async sendCommunication(payload: SendCommunicationPayload): Promise<AxiosResponse<ApiResponse<SendCommunicationResult>>> {
    return this.api.post('/communications/send', payload);
  }

  /** Erneut senden — nur für kind='manual' + delivery_status='failed' (sonst 409). */
  async resendCommunication(id: number): Promise<AxiosResponse<ApiResponse<ResendCommunicationResult>>> {
    return this.api.post(`/communications/${id}/resend`);
  }

  /** Anzahl fehlgeschlagener Sends seit `since` (Dashboard-Badge). */
  async getCommunicationsFailedCount(since?: string): Promise<AxiosResponse<ApiResponse<CommunicationFailedCount>>> {
    return this.api.get('/communications/failed-count', { params: since ? { since } : undefined });
  }

  /** Vendor-Feature-Flags (Paket-Steuerung) — Map + is_vendor für den Toggle-Tab. */
  async getFeatures(): Promise<AxiosResponse<ApiResponse<FeaturesResult>>> {
    return this.api.get('/features');
  }

  /** Flags ändern — nur Vendor-Accounts (VENDOR_USERS), sonst 403. */
  async updateFeatures(patch: Partial<Record<string, boolean>>): Promise<AxiosResponse<ApiResponse<FeaturesResult>>> {
    return this.api.put('/features', patch);
  }

  // --- SmartAI (KI-Rezeption): Wissensbasis + Betriebs-Settings, admin-only ---

  async listSmartAIKnowledge(): Promise<AxiosResponse<{ success: boolean; entries: SmartAIKnowledgeEntry[]; categories: string[]; estimatedTokens: number }>> {
    return this.api.get('/smartai/knowledge');
  }

  async createSmartAIKnowledge(data: SmartAIKnowledgeInput): Promise<AxiosResponse<{ success: boolean; entry: SmartAIKnowledgeEntry }>> {
    return this.api.post('/smartai/knowledge', data);
  }

  async updateSmartAIKnowledge(id: number, data: Partial<SmartAIKnowledgeInput>): Promise<AxiosResponse<{ success: boolean; entry: SmartAIKnowledgeEntry }>> {
    return this.api.put(`/smartai/knowledge/${id}`, data);
  }

  async deleteSmartAIKnowledge(id: number): Promise<AxiosResponse<{ success: boolean }>> {
    return this.api.delete(`/smartai/knowledge/${id}`);
  }

  async getSmartAIStats(): Promise<AxiosResponse<{
    success: boolean;
    today: { calls: number; prompt_tokens: number; completion_tokens: number };
    dailyCallLimit: number;
    recent: Array<{ day: string; calls: number; prompt_tokens: number; completion_tokens: number }>;
    requestsByKind: Record<string, number>;
  }>> {
    return this.api.get('/smartai/stats');
  }

  async getSmartAISettings(): Promise<AxiosResponse<{ success: boolean; settings: SmartAISettings }>> {
    return this.api.get('/smartai/settings');
  }

  async updateSmartAISettings(patch: SmartAISettingsPatch): Promise<AxiosResponse<{ success: boolean; settings: SmartAISettings }>> {
    return this.api.put('/smartai/settings', patch);
  }

  /** Verbindungstest gegen den KI-Anbieter. Ungespeicherte Werte dürfen mit. */
  async testSmartAILLM(
    payload: { model?: string; apiKey?: string; baseUrl?: string; reasoningEffort?: SmartAIReasoningEffort },
  ): Promise<AxiosResponse<{ success: boolean; result: SmartAILlmTestResult }>> {
    return this.api.post('/smartai/test-llm', payload);
  }

  /** Posteingang (Phase 2a) — gefilterte Liste + Zähler für die Filter-Pills. */
  async listInbox(
    params?: { state?: CommunicationInboxState | 'all'; limit?: number; before_id?: number },
  ): Promise<AxiosResponse<ApiResponse<InboxListResult>>> {
    return this.api.get('/communications/inbox', { params });
  }

  /** Inbox-Zustand setzen (gelesen/beantwortet/neu) — 409 bei unmatched. */
  async setCommunicationState(
    id: number,
    state: 'read' | 'answered' | 'new',
  ): Promise<AxiosResponse<ApiResponse<Communication>>> {
    return this.api.patch(`/communications/${id}/state`, { state });
  }

  /** Unzugeordnete Inbound-Mail einem Patienten zuordnen (optional Adresse als Zweit-E-Mail). */
  async assignCommunicationPatient(
    id: number,
    patientId: number,
    saveAlternateEmail: boolean,
  ): Promise<AxiosResponse<ApiResponse<AssignCommunicationResult>>> {
    return this.api.post(`/communications/${id}/assign`, {
      patient_id: patientId,
      save_alternate_email: saveAlternateEmail,
    });
  }

  /** Anhang einer Inbound-Mail als Blob laden (mit Auth-Header, Muster fetchInvoicePdf). */
  async fetchCommunicationAttachment(id: number, index: number): Promise<Blob> {
    const res = await this.api.get(`/communications/${id}/attachment/${index}`, {
      responseType: 'blob',
      timeout: 30000,
    });
    return res.data;
  }

  // ----- Treatment Notes -----

  async listTreatmentNotes(patientId: number, params?: { includeArchived?: boolean }): Promise<AxiosResponse<ApiResponse<TreatmentNote[]>>> {
    return this.api.get(`/patients/${patientId}/notes`, {
      params: params?.includeArchived ? { includeArchived: 'true' } : undefined,
    });
  }

  async createTreatmentNote(patientId: number, payload: CreateTreatmentNotePayload): Promise<AxiosResponse<ApiResponse<TreatmentNote>>> {
    return this.api.post(`/patients/${patientId}/notes`, payload);
  }

  async updateTreatmentNote(id: number, payload: UpdateTreatmentNotePayload): Promise<AxiosResponse<ApiResponse<TreatmentNote>>> {
    return this.api.put(`/notes/${id}`, payload);
  }

  async archiveTreatmentNote(id: number, reason?: string): Promise<AxiosResponse<ApiResponse<TreatmentNote>>> {
    return this.api.delete(`/notes/${id}`, { data: { reason } });
  }

  async unarchiveTreatmentNote(id: number): Promise<AxiosResponse<ApiResponse<TreatmentNote>>> {
    return this.api.post(`/notes/${id}/unarchive`);
  }

  async getTreatmentNoteAuditLogs(id: number): Promise<AxiosResponse<ApiResponse<TreatmentNoteAuditLog[]>>> {
    return this.api.get(`/notes/${id}/audit-logs`);
  }

  // ----- Treatment Note Templates (Phase A.5) -----

  async listTreatmentNoteTemplates(params?: { includeArchived?: boolean; category?: TemplateCategory }): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate[]>>> {
    const q: Record<string, string> = {};
    if (params?.includeArchived) q.includeArchived = 'true';
    if (params?.category) q.category = params.category;
    return this.api.get('/note-templates', { params: q });
  }

  async getTreatmentNoteTemplate(idOrSlug: number | string): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate>>> {
    return this.api.get(`/note-templates/${idOrSlug}`);
  }

  async createTreatmentNoteTemplate(payload: CreateTemplatePayload): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate>>> {
    return this.api.post('/note-templates', payload);
  }

  async updateTreatmentNoteTemplate(id: number, payload: UpdateTemplatePayload): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate>>> {
    return this.api.put(`/note-templates/${id}`, payload);
  }

  async archiveTreatmentNoteTemplate(id: number, reason?: string): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate>>> {
    return this.api.delete(`/note-templates/${id}`, { data: { reason } });
  }

  async unarchiveTreatmentNoteTemplate(id: number): Promise<AxiosResponse<ApiResponse<TreatmentNoteTemplate>>> {
    return this.api.post(`/note-templates/${id}/unarchive`);
  }

  async listIndications(): Promise<AxiosResponse<ApiResponse<Indication[]>>> {
    return this.api.get('/indications');
  }

  // ----- Procedure Suggestions (Phase D) -----

  async suggestProceduresForTemplate(
    templateId: number,
    sectionValues: Record<string, any>,
  ): Promise<AxiosResponse<ApiResponse<{ suggestions: ProcedureSuggestion[] }>>> {
    return this.api.post(`/note-templates/${templateId}/suggest-procedures`, {
      section_values: sectionValues,
    });
  }

  async suggestProceduresForNote(
    noteId: number,
    sectionValues?: Record<string, any>,
  ): Promise<AxiosResponse<ApiResponse<{ suggestions: ProcedureSuggestion[] }>>> {
    return this.api.post(`/notes/${noteId}/suggest-procedures`, sectionValues
      ? { section_values: sectionValues }
      : {});
  }

  // ----- Audio (Phase B) -----

  async uploadAudio(noteId: number, blob: Blob, opts?: { locale?: string; filename?: string }): Promise<AxiosResponse<ApiResponse<AudioFile>>> {
    const form = new FormData();
    const ext = blob.type.includes('ogg') ? 'ogg'
      : blob.type.includes('webm') ? 'webm'
      : blob.type.includes('mp4') ? 'm4a'
      : blob.type.includes('wav') ? 'wav'
      : 'bin';
    form.append('file', blob, opts?.filename || `recording.${ext}`);
    if (opts?.locale) form.append('locale', opts.locale);
    return this.api.post(`/notes/${noteId}/audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async listAudioForNote(noteId: number): Promise<AxiosResponse<ApiResponse<AudioFile[]>>> {
    return this.api.get(`/notes/${noteId}/audio`);
  }

  async getAudio(id: number): Promise<AxiosResponse<ApiResponse<AudioFile>>> {
    return this.api.get(`/audio/${id}`);
  }

  async getAudioConsent(patientId: number): Promise<AxiosResponse<ApiResponse<AudioConsentResponse>>> {
    return this.api.get(`/admin/intake/by-patient/${patientId}/audio-consent`);
  }

  async setAudioConsent(patientId: number, granted: boolean): Promise<AxiosResponse<ApiResponse<AudioConsentResponse>>> {
    return this.api.post(`/admin/intake/by-patient/${patientId}/audio-consent`, { granted });
  }

  // ----- Event Log / API Monitoring -----

  async getEventLogs(params?: { type?: string; status?: string; method?: string; path?: string }): Promise<AxiosResponse> {
    return this.api.get('/admin/event-log', { params });
  }

  async getEventLogStats(): Promise<AxiosResponse> {
    return this.api.get('/admin/event-log/stats');
  }

  // ---- Automatische Termin-Erinnerungen ----
  async getReminderSettings(): Promise<AxiosResponse> {
    return this.api.get('/reminders/settings');
  }

  async updateReminderSettings(payload: { enabled: boolean; tiers: { key: string; enabled: boolean }[] }): Promise<AxiosResponse> {
    return this.api.put('/reminders/settings', payload);
  }

  async getReminderLog(params?: { tier?: string; status?: string; trigger?: string }): Promise<AxiosResponse> {
    return this.api.get('/reminders/log', { params });
  }

  async getUpcomingReminders(): Promise<AxiosResponse> {
    return this.api.get('/reminders/upcoming');
  }

  async sendReminder(appointmentId: number, tier: string): Promise<AxiosResponse> {
    return this.api.post('/reminders/send', { appointmentId, tier });
  }

  async runReminders(dryRun = false): Promise<AxiosResponse> {
    return this.api.post('/reminders/run', { dryRun });
  }

  // Self-Service-Aktionen von Patienten (Absagen/Umbuchungen) fürs Dashboard
  async getPatientActions(days = 7, includeAcknowledged = false): Promise<AxiosResponse> {
    return this.api.get('/appointments/patient-actions', {
      params: { days, ...(includeAcknowledged ? { include_acknowledged: 1 } : {}) },
    });
  }

  // Eine Self-Service-Aktion als erledigt markieren bzw. die Quittung zurücknehmen.
  async acknowledgePatientAction(auditId: number): Promise<AxiosResponse> {
    return this.api.post(`/appointments/patient-actions/${auditId}/acknowledge`);
  }

  async unacknowledgePatientAction(auditId: number): Promise<AxiosResponse> {
    return this.api.post(`/appointments/patient-actions/${auditId}/unacknowledge`);
  }

  // ---- Recall-System (Wiedereinbestellung) ----
  async getRecallDue(horizonDays = 30): Promise<AxiosResponse> {
    return this.api.get('/recalls/due', { params: { horizonDays } });
  }

  async getRecallStats(days = 90): Promise<AxiosResponse> {
    return this.api.get('/recalls/stats', { params: { days } });
  }

  async getRecallSettings(): Promise<AxiosResponse> {
    return this.api.get('/recalls/settings');
  }

  async updateRecallSettings(payload: any): Promise<AxiosResponse> {
    return this.api.put('/recalls/settings', payload);
  }

  async sendRecall(scheduleId: number): Promise<AxiosResponse> {
    return this.api.post(`/recalls/${scheduleId}/send`);
  }

  async dismissRecall(scheduleId: number): Promise<AxiosResponse> {
    return this.api.post(`/recalls/${scheduleId}/dismiss`);
  }

  async runRecalls(dryRun = false): Promise<AxiosResponse> {
    return this.api.post('/recalls/run', { dryRun });
  }

  // Additional utility methods for backward compatibility
  async getUpcomingAppointments(days = 7, providerId?: number): Promise<AxiosResponse<ApiResponse<Appointment[]>>> {
    return this.api.get('/appointments/upcoming', { params: { days, providerId } });
  }

  setAuthToken(token: string): void {
    setToken(token);
  }

  removeAuthToken(): void {
    removeToken();
  }

  // ----- Invoices (billing) -----

  async listInvoices(params?: InvoiceListParams): Promise<AxiosResponse<ApiResponse<InvoiceDocument[]>>> {
    return this.api.get('/invoices', { params });
  }

  async getInvoice(id: number): Promise<AxiosResponse<ApiResponse<InvoiceDocumentDetail>>> {
    return this.api.get(`/invoices/${id}`);
  }

  async createInvoice(payload: CreateInvoicePayload): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post('/invoices', payload);
  }

  async updateInvoice(id: number, payload: Partial<InvoiceDocument>): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.put(`/invoices/${id}`, payload);
  }

  async deleteInvoice(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/invoices/${id}`);
  }

  async addInvoiceItem(invoiceId: number, item: Partial<InvoiceItem>): Promise<AxiosResponse<ApiResponse<InvoiceItem>>> {
    return this.api.post(`/invoices/${invoiceId}/items`, item);
  }

  async updateInvoiceItem(invoiceId: number, itemId: number, item: Partial<InvoiceItem>): Promise<AxiosResponse<ApiResponse<InvoiceItem>>> {
    return this.api.put(`/invoices/${invoiceId}/items/${itemId}`, item);
  }

  async deleteInvoiceItem(invoiceId: number, itemId: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/invoices/${invoiceId}/items/${itemId}`);
  }

  async reorderInvoiceItems(invoiceId: number, order: number[]): Promise<AxiosResponse<ApiResponse<InvoiceItem[]>>> {
    return this.api.put(`/invoices/${invoiceId}/items/reorder`, { order });
  }

  // Status transitions
  async finalizeInvoice(id: number): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post(`/invoices/${id}/finalize`);
  }

  async acceptOffer(id: number): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post(`/invoices/${id}/accept`);
  }

  async rejectOffer(id: number): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post(`/invoices/${id}/reject`);
  }

  async convertOfferToInvoice(id: number): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post(`/invoices/${id}/convert-to-invoice`);
  }

  async cancelInvoice(id: number, reason?: string): Promise<AxiosResponse<ApiResponse<{ canceled: InvoiceDocument; cancellation: InvoiceDocument }>>> {
    return this.api.post(`/invoices/${id}/cancel`, { reason });
  }

  async markInvoicePaid(id: number, payload?: { paid_at?: string; payment_method?: string }): Promise<AxiosResponse<ApiResponse<InvoiceDocument>>> {
    return this.api.post(`/invoices/${id}/mark-paid`, payload || {});
  }

  async previewInvoiceEmail(id: number, language?: InvoiceLanguage): Promise<AxiosResponse<ApiResponse<InvoiceEmailPreview>>> {
    return this.api.get(`/invoices/${id}/email-preview`, { params: language ? { language } : {} });
  }

  async sendInvoiceEmail(id: number, payload: { to: string; subject: string; body: string }): Promise<AxiosResponse<ApiResponse<{ messageId: string; accepted: string[]; rejected: string[] }>>> {
    return this.api.post(`/invoices/${id}/send-email`, payload);
  }

  /** Live-rendered PDF (works for drafts). */
  invoicePreviewUrl(id: number): string {
    return `${API_BASE_URL}/invoices/${id}/pdf/preview`;
  }

  /** Persisted PDF; for drafts the backend falls back to live render. */
  invoicePdfUrl(id: number): string {
    return `${API_BASE_URL}/invoices/${id}/pdf`;
  }

  async fetchInvoicePdf(id: number, opts: { preview?: boolean } = {}): Promise<Blob> {
    const path = opts.preview ? `/invoices/${id}/pdf/preview` : `/invoices/${id}/pdf`;
    const res = await this.api.get(path, { responseType: 'blob', timeout: 30000 });
    return res.data;
  }

  // Catalog
  async listInvoiceCatalog(params?: { activeOnly?: boolean; category?: string; search?: string }): Promise<AxiosResponse<ApiResponse<InvoiceCatalogItem[]>>> {
    return this.api.get('/invoices/catalog', { params });
  }

  async getInvoiceCatalogItem(id: number): Promise<AxiosResponse<ApiResponse<InvoiceCatalogItem>>> {
    return this.api.get(`/invoices/catalog/${id}`);
  }

  async createInvoiceCatalogItem(item: Partial<InvoiceCatalogItem>): Promise<AxiosResponse<ApiResponse<InvoiceCatalogItem>>> {
    return this.api.post('/invoices/catalog', item);
  }

  async updateInvoiceCatalogItem(id: number, item: Partial<InvoiceCatalogItem>): Promise<AxiosResponse<ApiResponse<InvoiceCatalogItem>>> {
    return this.api.put(`/invoices/catalog/${id}`, item);
  }

  async deleteInvoiceCatalogItem(id: number): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.api.delete(`/invoices/catalog/${id}`);
  }

  // Counters
  async getInvoiceCounters(): Promise<AxiosResponse<ApiResponse<{ counters: Record<InvoiceType, InvoiceCounter>; previews: Record<InvoiceType, InvoiceCounterPreview> }>>> {
    return this.api.get('/invoices/counters');
  }

  async updateInvoiceCounter(type: InvoiceType, patch: Partial<InvoiceCounter>): Promise<AxiosResponse<ApiResponse<InvoiceCounter>>> {
    return this.api.put(`/invoices/counters/${type}`, patch);
  }

  // Billing settings
  async getBillingSettings(): Promise<AxiosResponse<ApiResponse<BillingSettings>>> {
    return this.api.get('/invoices/settings');
  }

  async updateBillingSettings(patch: Partial<BillingSettings>): Promise<AxiosResponse<ApiResponse<BillingSettings>>> {
    return this.api.put('/invoices/settings', patch);
  }

  async getBillingSmtp(): Promise<AxiosResponse<ApiResponse<BillingSmtpSettings>>> {
    return this.api.get('/invoices/settings/account');
  }

  async updateBillingSmtp(patch: Partial<BillingSmtpSettings> & { pass?: string }): Promise<AxiosResponse<ApiResponse<BillingSmtpSettings>>> {
    return this.api.put('/invoices/settings/account', patch);
  }

  async testBillingSmtp(to: string): Promise<AxiosResponse<ApiResponse<{ messageId: string; accepted: string[]; rejected: string[] }>>> {
    return this.api.post('/invoices/settings/account/test', { to });
  }

  // Betriebszustand der Instanz. Antwortet flach (nicht im ApiResponse-Umschlag),
  // deshalb eine eigene Methode statt der generischen get().
  async getHealth(): Promise<AxiosResponse<HealthStatus>> {
    return this.api.get('/health');
  }

  // Generic methods for backward compatibility
  async get<T>(url: string, params?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.get(url, { params });
  }

  async post<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.post(url, data);
  }

  async put<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.put(url, data);
  }

  async patch<T>(url: string, data?: any): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.patch(url, data);
  }

  async delete<T>(url: string): Promise<AxiosResponse<ApiResponse<T>>> {
    return this.api.delete(url);
  }
}

export const apiService = new ApiService();
export default apiService; 